import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Sparkles, CheckCircle2, Loader2, Eye, Search, Share2, FileText, Smartphone, Moon, Type, ChevronDown, ChevronUp, Settings2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import ContentBlockRenderer from "@/components/blog/ContentBlockRenderer";

const STEPS = [
  "Carregando agente...",
  "Construindo prompt composto...",
  "Gerando conteúdo estruturado...",
  "Criando imagem de capa...",
  "Montando post...",
  "Salvando no banco...",
  "Concluído!",
];

const AdminCreate = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<string>("");
  const [generating, setGenerating] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [result, setResult] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState<Record<string, boolean>>({});
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customTopic, setCustomTopic] = useState("");
  const [customTone, setCustomTone] = useState("");
  const [customLength, setCustomLength] = useState("");
  const [customNotes, setCustomNotes] = useState("");

  useEffect(() => {
    supabase.from("agents").select("*").eq("is_active", true).order("name").then(({ data }) => {
      setAgents(data || []);
    });
  }, []);

  const selectedAgentData = agents.find((a) => a.id === selectedAgent);

  const toggle = (key: string) => setPreviewOpen((p) => ({ ...p, [key]: !p[key] }));

  const generate = async () => {
    if (!selectedAgent) {
      toast({ title: "Selecione um agente", variant: "destructive" });
      return;
    }
    setGenerating(true);
    setResult(null);
    setCurrentStep(0);

    const stepInterval = setInterval(() => {
      setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 2));
    }, 3000);

    try {
      const { data, error } = await supabase.functions.invoke("generate-post-from-agent", {
        body: {
          agent_id: selectedAgent,
          ...(customTopic && { topic: customTopic }),
          ...(customTone && { tone_override: customTone }),
          ...(customLength && { length_override: customLength }),
          ...(customNotes && { extra_instructions: customNotes }),
        },
      });

      clearInterval(stepInterval);
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      setCurrentStep(STEPS.length - 1);
      setResult(data);
      toast({ title: "Post gerado com sucesso!" });
    } catch (e: any) {
      clearInterval(stepInterval);
      toast({ title: "Erro na geração", description: e.message, variant: "destructive" });
      setGenerating(false);
      setCurrentStep(0);
    }
  };

  const publishPost = async () => {
    if (!result?.post?.id) return;
    await supabase.from("posts").update({ status: "published", published_at: new Date().toISOString() }).eq("id", result.post.id);
    toast({ title: "Post publicado!" });
    navigate("/admin/library");
  };

  const progress = ((currentStep + 1) / STEPS.length) * 100;
  const structured = result?.structured || result?.post?.content_json;

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-8 text-center">
        <Sparkles className="mx-auto mb-3 h-10 w-10 text-primary" />
        <h1 className="text-2xl font-bold text-foreground">Criar Post</h1>
        <p className="text-sm text-muted-foreground">Selecione um agente e gere com 1 clique</p>
      </div>

      {/* AGENT SELECTION */}
      {!generating && !result && (
        <div className="space-y-6">
          <div className="rounded-xl border border-border bg-card p-6">
            <label className="mb-2 block text-sm font-medium text-foreground">Agente</label>
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Selecione um agente..." />
              </SelectTrigger>
              <SelectContent>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name} — {agent.niche}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedAgentData && (
              <div className="mt-4 space-y-1 rounded-lg bg-muted/50 p-3 text-sm text-muted-foreground">
                <p><strong>Tom:</strong> {selectedAgentData.tone_of_voice}</p>
                <p><strong>Público:</strong> {selectedAgentData.target_audience || "—"}</p>
                <p><strong>Tamanho:</strong> {selectedAgentData.article_length}</p>
              </div>
            )}

            {/* Optional overrides */}
            <div className="mt-4">
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
              >
                <Settings2 className="h-4 w-4" />
                Personalizar geração (opcional)
                {showAdvanced ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
              </button>

              {showAdvanced && (
                <div className="mt-3 space-y-3 rounded-lg border border-border bg-muted/30 p-4">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-foreground">Tema / Assunto específico</label>
                    <Input
                      placeholder='Ex: "O poder da oração", "Frutos do Espírito"...'
                      value={customTopic}
                      onChange={(e) => setCustomTopic(e.target.value)}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="mb-1 block text-xs font-medium text-foreground">Tom (override)</label>
                      <Select value={customTone} onValueChange={setCustomTone}>
                        <SelectTrigger><SelectValue placeholder="Usar do agente" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pastoral">Pastoral</SelectItem>
                          <SelectItem value="didatico">Didático</SelectItem>
                          <SelectItem value="inspiracional">Inspiracional</SelectItem>
                          <SelectItem value="reflexivo">Reflexivo</SelectItem>
                          <SelectItem value="evangelistico">Evangelístico</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-medium text-foreground">Tamanho (override)</label>
                      <Select value={customLength} onValueChange={setCustomLength}>
                        <SelectTrigger><SelectValue placeholder="Usar do agente" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="curto">Curto (600-900)</SelectItem>
                          <SelectItem value="medio">Médio (1000-1500)</SelectItem>
                          <SelectItem value="longo">Longo (1800-2500)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-foreground">Instruções adicionais</label>
                    <Textarea
                      placeholder="Ex: Focar em jovens, incluir exemplos práticos, citar Salmos..."
                      value={customNotes}
                      onChange={(e) => setCustomNotes(e.target.value)}
                      rows={2}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <Button className="w-full py-6 text-base" size="lg" onClick={generate} disabled={!selectedAgent}>
            <Sparkles className="mr-2 h-5 w-5" /> Gerar Post
          </Button>
        </div>
      )}

      {/* LOADING */}
      {generating && !result && (
        <div className="rounded-xl border border-border bg-card p-8 text-center">
          <Loader2 className="mx-auto mb-4 h-10 w-10 animate-spin text-primary" />
          <p className="mb-4 text-lg font-medium text-foreground">{STEPS[currentStep]}</p>
          <Progress value={progress} className="mx-auto max-w-md" />
          <p className="mt-2 text-xs text-muted-foreground">Etapa {currentStep + 1} de {STEPS.length}</p>
        </div>
      )}

      {/* RESULT WITH STRUCTURED PREVIEW */}
      {result && (
        <div className="space-y-6">
          {/* Status Badge */}
          <div className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/30">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-amber-600" />
              <div>
                <p className="font-semibold text-foreground">Revisão Pendente</p>
                <p className="text-xs text-muted-foreground">Tempo: {((result.generation_time_ms || 0) / 1000).toFixed(1)}s</p>
              </div>
            </div>
            <Badge variant="outline" className="border-amber-400 text-amber-700">pending_review</Badge>
          </div>

          {/* Tabs */}
          <Tabs defaultValue="preview" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="preview"><Eye className="mr-1.5 h-4 w-4" />Preview</TabsTrigger>
              <TabsTrigger value="seo"><Search className="mr-1.5 h-4 w-4" />SEO</TabsTrigger>
              <TabsTrigger value="social"><Share2 className="mr-1.5 h-4 w-4" />Social</TabsTrigger>
              <TabsTrigger value="technical"><FileText className="mr-1.5 h-4 w-4" />Técnico</TabsTrigger>
            </TabsList>

            {/* PREVIEW TAB */}
            <TabsContent value="preview" className="space-y-4">
              {/* Cover */}
              <div className="rounded-xl border border-border bg-card overflow-hidden">
                {result.post?.cover_image_url && (
                  <img src={result.post.cover_image_url} alt={structured?.cover?.alt_text || ""} className="w-full object-cover" style={{ maxHeight: 320 }} />
                )}
                <div className="p-6">
                  <h1 className="mb-2 text-2xl font-bold text-foreground">{result.post?.title}</h1>
                  <p className="mb-3 text-muted-foreground">{structured?.executive_summary || result.post?.excerpt}</p>
                  {structured?.preview_layer?.reading_time_estimate && (
                    <Badge variant="secondary">⏱ {structured.preview_layer.reading_time_estimate}</Badge>
                  )}
                </div>
              </div>

              {/* Content Blocks */}
              {structured?.content_blocks && (
                <div className="rounded-xl border border-border bg-card p-6">
                  <ContentBlockRenderer blocks={structured.content_blocks} />
                </div>
              )}

              {/* Internal Images */}
              {structured?.internal_images?.length > 0 && (
                <CollapsibleSection title="Imagens Internas" isOpen={previewOpen.images} onToggle={() => toggle("images")}>
                  <div className="grid gap-3 sm:grid-cols-3">
                    {structured.internal_images.map((img: any, i: number) => (
                      <div key={i} className="rounded-lg border border-dashed border-border bg-muted/20 p-3 text-center">
                        <p className="text-xs font-medium text-foreground">Imagem {i + 1}</p>
                        <p className="mt-1 text-xs text-muted-foreground">{img.alt_text}</p>
                        <p className="mt-1 text-xs italic text-muted-foreground/60">{img.suggested_style}</p>
                      </div>
                    ))}
                  </div>
                </CollapsibleSection>
              )}
            </TabsContent>

            {/* SEO TAB */}
            <TabsContent value="seo" className="space-y-4">
              {/* Google Snippet Preview */}
              {structured?.google_snippet_preview && (
                <div className="rounded-xl border border-border bg-card p-6">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Google Snippet Preview</h3>
                  <div className="rounded-lg border border-border bg-background p-4">
                    <p className="text-lg text-blue-700 hover:underline">{structured.google_snippet_preview.simulated_title}</p>
                    <p className="text-xs text-green-700">{structured.google_snippet_preview.simulated_url}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{structured.google_snippet_preview.simulated_description}</p>
                  </div>
                </div>
              )}

              {/* Meta Description */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Meta Description</h3>
                <p className="text-sm text-foreground">{structured?.meta_description || result.post?.meta_description}</p>
                <p className="mt-1 text-xs text-muted-foreground">{(structured?.meta_description || "").length}/155 caracteres</p>
              </div>

              {/* Keywords */}
              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Keywords</h3>
                <div className="flex flex-wrap gap-2">
                  {(result.post?.seo_keywords || "").split(",").map((kw: string, i: number) => (
                    <Badge key={i} variant="secondary">{kw.trim()}</Badge>
                  ))}
                </div>
                {structured?.secondary_keywords?.length > 0 && (
                  <div className="mt-3">
                    <p className="mb-1 text-xs text-muted-foreground">Secundárias:</p>
                    <div className="flex flex-wrap gap-1">
                      {structured.secondary_keywords.map((kw: string, i: number) => (
                        <Badge key={i} variant="outline" className="text-xs">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Structure Outline */}
              {structured?.preview_layer?.structure_outline && (
                <div className="rounded-xl border border-border bg-card p-6">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Estrutura do Artigo</h3>
                  <ul className="space-y-1">
                    {structured.preview_layer.structure_outline.map((h: string, i: number) => (
                      <li key={i} className={`text-sm text-foreground ${h.startsWith("H1") ? "font-bold" : h.startsWith("H2") ? "ml-4 font-medium" : "ml-8 text-muted-foreground"}`}>
                        {h}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>

            {/* SOCIAL TAB */}
            <TabsContent value="social" className="space-y-4">
              {structured?.social_preview && (
                <div className="rounded-xl border border-border bg-card p-6">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Social Media Preview</h3>
                  <div className="rounded-lg border border-border bg-background overflow-hidden">
                    {result.post?.cover_image_url && (
                      <div className="relative">
                        <img src={result.post.cover_image_url} alt="" className="w-full object-cover" style={{ maxHeight: 200 }} />
                        <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 to-transparent p-4">
                          <p className="text-sm font-bold text-white">{structured.social_preview.simulated_og_text_overlay}</p>
                        </div>
                      </div>
                    )}
                    <div className="p-4">
                      <p className="font-bold text-foreground">{structured.social_preview.headline}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{structured.social_preview.description}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Cover Image Info */}
              {structured?.cover && (
                <div className="rounded-xl border border-border bg-card p-6">
                  <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Capa do Post</h3>
                  <div className="space-y-2 text-sm">
                    <p><strong className="text-foreground">Alt text:</strong> <span className="text-muted-foreground">{structured.cover.alt_text}</span></p>
                    <p><strong className="text-foreground">Estilo:</strong> <span className="text-muted-foreground">{structured.cover.suggested_style}</span></p>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* TECHNICAL TAB */}
            <TabsContent value="technical" className="space-y-4">
              {structured?.preview_layer && (
                <>
                  <PreviewCard icon={<Smartphone className="h-4 w-4" />} title="Mobile View" content={structured.preview_layer.mobile_view_simulation} />
                  <PreviewCard icon={<Moon className="h-4 w-4" />} title="Dark Mode" content={structured.preview_layer.dark_mode_simulation} />
                  <PreviewCard icon={<Type className="h-4 w-4" />} title="Tipografia" content={structured.preview_layer.typography_simulation} />
                </>
              )}

              <div className="rounded-xl border border-border bg-card p-6">
                <h3 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">Metadados</h3>
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>Slug: <code className="text-foreground">{result.post?.slug}</code></p>
                  <p>Status: <code className="text-foreground">{result.post?.status}</code></p>
                  <p>Blocos: <code className="text-foreground">{structured?.content_blocks?.length || 0}</code></p>
                  <p>Imagens internas: <code className="text-foreground">{structured?.internal_images?.length || 0}</code></p>
                </div>
              </div>
            </TabsContent>
          </Tabs>

          {/* Actions */}
          <div className="flex gap-3">
            <Button className="flex-1" onClick={publishPost}>
              <CheckCircle2 className="mr-2 h-4 w-4" /> Aprovar e Publicar
            </Button>
            <Button variant="outline" className="flex-1" onClick={() => navigate(`/admin/posts/${result.post?.id}/edit`)}>Editar</Button>
            <Button variant="ghost" onClick={() => { setResult(null); setGenerating(false); setCurrentStep(0); }}>Gerar outro</Button>
          </div>
        </div>
      )}
    </div>
  );
};

const PreviewCard = ({ icon, title, content }: { icon: React.ReactNode; title: string; content: string }) => (
  <div className="rounded-xl border border-border bg-card p-5">
    <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-foreground">{icon} {title}</div>
    <p className="text-sm text-muted-foreground">{content}</p>
  </div>
);

const CollapsibleSection = ({ title, isOpen, onToggle, children }: { title: string; isOpen?: boolean; onToggle: () => void; children: React.ReactNode }) => (
  <div className="rounded-xl border border-border bg-card">
    <button onClick={onToggle} className="flex w-full items-center justify-between p-4 text-left text-sm font-semibold text-foreground">
      {title}
      {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
    </button>
    {isOpen && <div className="border-t border-border p-4">{children}</div>}
  </div>
);

export default AdminCreate;
